import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table,
  Form,
  Button,
  Modal,
  Image,
  Alert,
  Spinner,
} from 'react-bootstrap';

const BASE_URL = 'https://drdprints.com/api';

const AdminSubMainCategories = () => {
  // State variables
  const [subMainCategories, setSubMainCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [subMainTitle, setSubMainTitle] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [message, setMessage] = useState('');
  const [variant, setVariant] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all sub main categories
  const fetchSubMainCategories = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/get-sub-main-category`);
      setSubMainCategories(response.data.sub_main_categories);
    } catch (error) {
      console.error('Error fetching sub main categories:', error);
      setMessage('Error fetching sub main categories.');
      setVariant('danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch categories for category selection dropdown
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/get-category`);
      if (response.status === 200 && response.data && response.data.categories) {
        setCategories(response.data.categories);
      } else {
        setMessage('Failed to load categories.');
        setVariant('danger');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setMessage('Failed to load categories.');
      setVariant('danger');
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchSubMainCategories();
    fetchCategories();
  }, []);

  // Clean up object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (imagePreview && typeof imagePreview === 'string' && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  // Helper function to get category name by ID
  const getCategoryName = (cat_id) => {
    const category = categories.find(
      (cat) => Number(cat.id) === Number(cat_id)
    );
    return category ? category.cat_title : 'Unknown Category';
  };

  // Handle category selection change
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    // Clear any existing errors for cat_id
    if (formErrors.cat_id) {
      setFormErrors((prevErrors) => ({ ...prevErrors, cat_id: null }));
    }
  };

  // Handle image file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setImagePreview(objectUrl);
    } else {
      setImagePreview(null);
    }
  };

  // Handle form submission for adding or updating
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    // Ensure that both the category and sub main title are selected
    if (!selectedCategory) {
      setMessage('Please select a category.');
      setVariant('danger');
      return; // Exit if no category selected
    }
  
    if (!subMainTitle) {
      setMessage('Please enter a sub main title.');
      setVariant('danger');
      return; // Exit if no sub main title provided
    }
  
    const formData = new FormData();
    formData.append('cat_id', selectedCategory.toString());
    formData.append('sub_main_title', subMainTitle);
    if (image) {
      formData.append('image', image);
    }
  
    // Debugging: Log the FormData contents
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }
  
    try {
      let response;
      if (currentCategoryId) {
        response = await axios.post(`${BASE_URL}/update-sub-main-category/${currentCategoryId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setMessage('Sub Main Category updated successfully!');
        setVariant('success');
      } else {
        response = await axios.post(`${BASE_URL}/store-sub-main-category`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setMessage('Sub Main Category added successfully!');
        setVariant('success');
      }
      console.log('Server response:', response.data);
      fetchSubMainCategories();
      handleCloseModal();
    } catch (error) {
      console.error('Error submitting sub main category:', error);
      setMessage(`Failed to submit sub main category: ${error.response?.data?.errors?.cat_id || error.response?.data?.errors?.sub_main_title || 'Unknown error'}`);
      setVariant('danger');
    } finally {
      setIsLoading(false);
    }
  };
  


  // Handle deletion of a sub main category
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this Sub Main Category?'
    );
    if (!confirmDelete) return;

    setIsLoading(true);

    try {
      await axios.delete(`${BASE_URL}/deletesubmaincategory/${id}`);
      setMessage('Sub Main Category deleted successfully!');
      setVariant('success');
      fetchSubMainCategories();
    } catch (error) {
      console.error('Error deleting sub main category:', error);
      setMessage('Failed to delete sub main category.');
      setVariant('danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Open modal for adding or updating
  const handleOpenModal = (category = null) => {
    if (category) {
      setCurrentCategoryId(category.id);
      setSelectedCategory(category.cat_id);
      setSubMainTitle(category.sub_main_title);
      setImagePreview(category.image_url);
      setImage(null); // Reset image file. If you want to keep existing image, handle accordingly
    } else {
      setCurrentCategoryId(null);
      setSelectedCategory('');
      setSubMainTitle('');
      setImagePreview(null);
      setImage(null);
    }
    setShowModal(true);
    setMessage('');
    setVariant('');
    setFormErrors({});
  };

  // Close modal and reset form states
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCategory('');
    setSubMainTitle('');
    setImage(null);
    setImagePreview(null);
    setCurrentCategoryId(null);
    setMessage('');
    setVariant('');
    setFormErrors({});
  };

  return (
    <div className="container mt-4">
      <h1>Sub Main Categories Management</h1>

      {/* Display success or error messages */}
      {message && (
        <Alert variant={variant} className="mt-3">
          {variant === 'danger' ? (
            // Preserve line breaks for multiple errors
            <pre style={{ whiteSpace: 'pre-wrap' }}>{message}</pre>
          ) : (
            message
          )}
        </Alert>
      )}

      {/* Add Sub Main Category Button */}
      <Button variant="primary" className="mt-3" onClick={() => handleOpenModal()}>
        Add Sub Main Category
      </Button>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="text-center my-3">
          <Spinner animation="border" role="status" />
        </div>
      )}

      {/* Sub Main Categories Table */}
      <Table striped bordered hover className="mt-3">
        <thead>
          <tr>
            <th>ID</th>
            <th>Category</th>
            <th>Title</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {subMainCategories.length > 0 ? (
            subMainCategories.map((category) => (
              <tr key={category.id}>
                <td>{category.id}</td>
                <td>{getCategoryName(category.cat_id)}</td>
                <td>{category.sub_main_title}</td>
                <td>
                  <Image
                    src={`https://drdprints.com/uploads/submainimage/${category.image}` || 'https://via.placeholder.com/100'}
                    thumbnail
                    style={{ maxWidth: '100px' }}
                    alt={category.sub_main_title}
                  />
                </td>
                <td>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => handleOpenModal(category)}
                    className="me-2"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(category.id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="text-center">
                No Sub Main Categories found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Modal for Adding/Updating Sub Main Category */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentCategoryId ? 'Update Sub Main Category' : 'Add Sub Main Category'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit} noValidate>
            {/* Category Selection */}
            <Form.Group controlId="formCategorySelect" className="mb-3">
              <Form.Label>Select Category</Form.Label>
              <Form.Control
                as="select"
                value={selectedCategory}
                onChange={handleCategoryChange}
                isInvalid={!!formErrors.cat_id}
                required
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.cat_title}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {formErrors.cat_id && formErrors.cat_id.join(', ')}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Sub Main Category Title */}
            <Form.Group controlId="formSubMainTitle" className="mb-3">
              <Form.Label>Sub Main Category Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter sub main category title"
                value={subMainTitle}
                onChange={(e) => setSubMainTitle(e.target.value)}
                isInvalid={!!formErrors.sub_main_title}
                required
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.sub_main_title && formErrors.sub_main_title.join(', ')}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Image Upload */}
            <Form.Group controlId="formImageUpload" className="mb-3">
              <Form.Label>Upload Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                isInvalid={!!formErrors.image}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.image && formErrors.image.join(', ')}
              </Form.Control.Feedback>
              {imagePreview && (
                <Image
                  src={imagePreview}
                  thumbnail
                  style={{ maxWidth: '200px', marginTop: '10px' }}
                  alt="Preview"
                />
              )}
            </Form.Group>

            {/* Submit Button */}
            <Button variant="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {currentCategoryId ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                <>
                  {currentCategoryId ? 'Update' : 'Add'} Sub Main Category
                </>
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminSubMainCategories;
